/// Is the Mac showing a license the Mac it is for? (docs/features/license-private-step-in-mac.md › Showing it;
/// docs/features/licenses.md › Stage 2.) A license names ONE Mac by a key — `macKey`, P-256, its private half
/// made in that Mac's Secure Enclave — and the site's signature covers it. A copied license file is therefore
/// worth nothing by itself: whoever shows it must sign a fresh challenge with that key.
///
///   client → Mac   a nonce, 32 random bytes, new for every question
///   Mac → client   the license text and the proof
///   proof    = ECDSA P-256 / SHA-256 by the license's Mac key over  header · digest
///   header   = "CODY-LICENSE-HOLDER-1\n"                                   (22 bytes)
///   digest   = SHA-256( license id · the Mac's identity public key · nonce )
///              id: the 32 bytes inside the license · identity key: its SPKI DER, 91 bytes — the very bytes this
///              client pinned at pairing (pairing.js keeps them base64url) · nonce: 32 bytes. Plain concatenation:
///              the first and the last part have a fixed length, so nothing can slide.
///   encoding = the signature is RAW r ‖ s, 64 bytes — what WebCrypto emits and what the Mac's identity
///              signatures already are (pairing.js `verifySignature`). NOT DER: a DER signature (70 to 72 bytes,
///              what Security.framework's SecKeyCreateSignature returns) is refused as `proof-malformed`. In
///              CryptoKit: `signature.rawRepresentation`; from SecKey's DER: `P256.Signing.ECDSASignature(
///              derRepresentation:).rawRepresentation`.
///
/// The identity key is in the digest so the proof is for THIS connection's Mac: the license's Mac key and the
/// Mac's identity key are different keys (the site sees the first inside a refundable license and must not learn
/// the second beside it), and the nonce keeps an old proof from being played again.
///
/// One seam for the clients: `verifyPresented(…)` → a plain object, never a throw. Offline, WebCrypto only.
/// `signHolder` is the Mac's half, here for the tests and the vectors (the Swift twin signs in the Secure Enclave).
import { LICENSE_KEYS } from './license-keys.js'
import { LicenseError, verify, join, unb64u, isP256 } from './license.js'

export const HOLDER_HEADER = 'CODY-LICENSE-HOLDER-1\n'
const text = new TextEncoder()
const bytes = (value) => (typeof value === 'string' ? unb64u(value) : value)

/// The bytes a Mac key signs for one question. id, identityKey (SPKI DER), nonce — bytes.
export async function holderBytes({ id, identityKey, nonce }) {
  if (!(id instanceof Uint8Array) || id.length !== 32) throw new LicenseError('malformed', 'the license id is 32 bytes')
  if (!isP256(identityKey)) throw new LicenseError('malformed', 'the identity key is a P-256 public key')
  if (!(nonce instanceof Uint8Array) || nonce.length !== 32) throw new LicenseError('malformed', 'the nonce is 32 bytes')
  return join(text.encode(HOLDER_HEADER), new Uint8Array(await crypto.subtle.digest('SHA-256', join(id, identityKey, nonce))))
}

/// The Mac's half: `privateKey` is a WebCrypto ECDSA P-256 signing key → the proof, raw r ‖ s (64 bytes).
export async function signHolder(privateKey, parts) {
  return new Uint8Array(await crypto.subtle.sign({ name: 'ECDSA', hash: 'SHA-256' }, privateKey, await holderBytes(parts)))
}

const no = (why, message) => ({ valid: false, why, message })

/// What a Mac showed, checked: the license's signature (the key list) → the revocation list as last seen → the
/// proof against the Mac key inside the license, over this license's id, the Mac's pinned identity key and the
/// nonce this client just sent.
///   license          the license text the Mac sent (or nothing)
///   proof            the holder proof: raw r ‖ s, as bytes or base64url
///   nonce            the 32 bytes this client sent, as bytes or base64url
///   macIdentityKey   the Mac's identity key this client pinned: SPKI DER, as bytes or base64url (as pinned)
///   keyList          the signing keys this client trusts: the built-in list when left out (an array, or {keys})
///   revoked          a list `verifyList` returned, or nothing (none seen yet)
///   modes            which keys count: 'live' only unless told (a dev build takes 'test' too)
/// → {valid: true, keyId, grants, mode, refundable, id} or {valid: false, why, message}; `why` is
///   no-license · malformed · unknown-key · distrusted · bad-signature · wrong-mode · revoked (the license itself)
///   · no-identity · nonce · no-proof · proof-malformed · mac-key · bad-proof (the Mac) · unsupported.
export async function verifyPresented({ license, proof, nonce, macIdentityKey, keyList = LICENSE_KEYS.keys, revoked = null, modes = ['live'] } = {}) {
  if (!license) return no('no-license', 'The Mac showed no license.')
  try {
    const good = await verify(String(license), { keys: Array.isArray(keyList) ? keyList : keyList?.keys || [], modes, revoked })
    let identity, asked, signature
    try { identity = bytes(macIdentityKey) } catch { /* below */ }
    if (!isP256(identity)) return no('no-identity', 'This device has no pinned key for the Mac to check the proof against.')
    try { asked = bytes(nonce) } catch { /* below */ }
    if (!(asked instanceof Uint8Array) || asked.length !== 32) return no('nonce', 'The nonce is not 32 bytes.')
    if (!proof?.length) return no('no-proof', 'The Mac showed a license and no proof that it is the Mac it is for.')
    try { signature = bytes(proof) } catch { /* below */ }
    if (!(signature instanceof Uint8Array) || signature.length !== 64) return no('proof-malformed', 'The proof is not a raw 64-byte signature.')
    let key
    try { key = await crypto.subtle.importKey('spki', good.macKey, { name: 'ECDSA', namedCurve: 'P-256' }, false, ['verify']) }
    catch { return no('mac-key', 'The Mac key inside the license is not a P-256 key.') }
    const held = await crypto.subtle.verify({ name: 'ECDSA', hash: 'SHA-256' }, key, signature, await holderBytes({ id: good.id, identityKey: identity, nonce: asked }))
    if (!held) return no('bad-proof', 'The proof does not check out: this license is for another Mac, or the answer is not to this question.')
    return { valid: true, keyId: good.keyId, grants: good.grants, mode: good.mode, refundable: good.refundable, id: good.idText }
  } catch (e) {
    if (e instanceof LicenseError) return no(e.code, e.message)
    return no('unsupported', 'This device could not check the license (' + (e?.message || e) + ').')
  }
}
